import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  LinearProgress,
  Badge,
} from "@mui/material";
import {
  Inventory,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Info,
  LocalDining,
  Kitchen,
  RestaurantMenu,
  Grain,
  Liquor,
  Fastfood,
  EmojiFoodBeverage,
  LocalBar,
  MoreVert,
  Refresh,
  Visibility,
  Edit,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Enhanced pantry type configurations
const pantryConfig = {
  GRAIN: {
    icon: Grain,
    color: "#8bc34a",
    gradient: "linear-gradient(135deg, #8bc34a, #689f38)",
    category: "Dry Goods",
    unit: "Boxes",
    goodThreshold: 10, // 10+ items = good stock
    lowThreshold: 5, // 3-9 items = low stock
    criticalThreshold: 2, // 1-2 items = critical
    maxCapacity: 20, // Assume max capacity for percentage calculation
    bgPattern: "🌾",
  },
  SPICE: {
    icon: Fastfood,
    color: "#ff9800",
    gradient: "linear-gradient(135deg, #ff9800, #f57c00)",
    category: "Seasonings",
    unit: "Boxes",
    goodThreshold: 8,
    lowThreshold: 4,
    criticalThreshold: 2,
    maxCapacity: 15,
    bgPattern: "🌶️",
  },
  OIL: {
    icon: Liquor,
    color: "#ffc107",
    gradient: "linear-gradient(135deg, #ffc107, #ff8f00)",
    category: "Liquids",
    unit: "Bottles",
    goodThreshold: 6,
    lowThreshold: 3,
    criticalThreshold: 1,
    maxCapacity: 12,
    bgPattern: "🛢️",
  },
  PASTE: {
    icon: LocalBar,
    color: "#e91e63",
    gradient: "linear-gradient(135deg, #e91e63, #c2185b)",
    category: "Sauces",
    unit: "Bottles",
    goodThreshold: 8,
    lowThreshold: 4,
    criticalThreshold: 2,
    maxCapacity: 15,
    bgPattern: "🍯",
  },
  MANUALBOWL: {
    icon: EmojiFoodBeverage,
    color: "#9c27b0",
    gradient: "linear-gradient(135deg, #9c27b0, #7b1fa2)",
    category: "Utensils",
    unit: "Pieces",
    goodThreshold: 15,
    lowThreshold: 8,
    criticalThreshold: 4,
    maxCapacity: 30,
    bgPattern: "🥣",
  },
  MANUALCUP: {
    icon: LocalDining,
    color: "#3f51b5",
    gradient: "linear-gradient(135deg, #3f51b5, #303f9f)",
    category: "Utensils",
    unit: "Pieces",
    goodThreshold: 20,
    lowThreshold: 10,
    criticalThreshold: 5,
    maxCapacity: 40,
    bgPattern: "☕",
  },
};

// Status helper function - matches PantryPage.jsx logic
const getStockStatus = (count, type) => {
  const config = pantryConfig[type] || pantryConfig.GRAIN;

  if (count === 0) {
    return {
      status: "OUT_OF_STOCK",
      color: "#f44336",
      label: "Out of Stock",
      icon: Error,
      severity: "critical",
      percentage: 0,
    };
  } else if (count <= config.criticalThreshold) {
    return {
      status: "CRITICAL",
      color: "#ff5722",
      label: "Critical Low",
      icon: Warning,
      severity: "critical",
      percentage: (count / config.maxCapacity) * 100,
    };
  } else if (count <= config.lowThreshold) {
    return {
      status: "LOW",
      color: "#ff9800",
      label: "Running Low",
      icon: TrendingDown,
      severity: "warning",
      percentage: (count / config.maxCapacity) * 100,
    };
  } else {
    return {
      status: "GOOD",
      color: "#4caf50",
      label: "In Stock",
      icon: CheckCircle,
      severity: "success",
      percentage: Math.min((count / config.maxCapacity) * 100, 100),
    };
  }
};

const PantryCard = React.memo(
  ({
    title,
    description,
    imageUrl,
    url,
    count = 0,
    inventoryType = "GRAIN",
    loading = false,
    utilization = null,
    aggregateStatus = null,
    totalBoxes = null,
    totalCapacity = null,
    totalFilled = null,
    hasComprehensiveData = false,
    boxes = [],
  }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [imageError, setImageError] = useState(false);
    const [showActions, setShowActions] = useState(false);

    const config = pantryConfig[inventoryType] || pantryConfig.GRAIN;

    // Use comprehensive data if available, otherwise fall back to count-based calculation
    let stockStatus, stockPercentage;

    if (
      hasComprehensiveData &&
      utilization !== null &&
      aggregateStatus !== null
    ) {
      // Use real box-based data
      stockPercentage = Math.round(utilization);

      switch (aggregateStatus) {
        case "OUT_OF_STOCK":
          stockStatus = {
            status: "OUT_OF_STOCK",
            color: "#f44336",
            label: "Out of Stock",
            icon: Error,
            severity: "critical",
            percentage: stockPercentage,
          };
          break;
        case "CRITICAL":
          stockStatus = {
            status: "CRITICAL",
            color: "#ff5722",
            label: "Critical Low",
            icon: Warning,
            severity: "critical",
            percentage: stockPercentage,
          };
          break;
        case "LOW":
          stockStatus = {
            status: "LOW",
            color: "#ff9800",
            label: "Running Low",
            icon: TrendingDown,
            severity: "warning",
            percentage: stockPercentage,
          };
          break;
        default:
          stockStatus = {
            status: "GOOD",
            color: "#4caf50",
            label: "Well Stocked",
            icon: CheckCircle,
            severity: "success",
            percentage: stockPercentage,
          };
      }
    } else {
      // Fall back to old count-based calculation
      stockStatus = getStockStatus(count, inventoryType);
      stockPercentage = stockStatus.percentage;
    }

    const IconComponent = config.icon;
    const StatusIcon = stockStatus.icon;

    if (loading) {
      return (
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            height: "100%",
            minHeight: 320,
          }}
        >
          <Skeleton variant="rectangular" height={160} />
          <CardContent sx={{ p: 2 }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width="80%" height={32} />
          </CardContent>
        </Card>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
        whileHover={{ y: -8, scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onHoverStart={() => setShowActions(true)}
        onHoverEnd={() => setShowActions(false)}
      >
        <Card
          component={Link}
          to={`/inventory-box/${url}`}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            height: "100%",
            minHeight: 320,
            textDecoration: "none",
            position: "relative",
            cursor: "pointer",
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[900]}90 100%)`
                : `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.grey[50]}90 100%)`,
            border: `1px solid ${theme.palette.divider}30`,
            backdropFilter: "blur(20px)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 20px 60px rgba(0,0,0,0.4), 0 0 30px ${config.color}40`
                  : `0 20px 60px rgba(0,0,0,0.15), 0 0 30px ${config.color}40`,
              border: `1px solid ${config.color}60`,
              "& .pantry-glow": {
                opacity: 0.08,
              },
              "& .status-indicator": {
                transform: "scale(1.1) rotate(5deg)",
              },
              "& .card-content": {
                background:
                  theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${theme.palette.background.paper}98 0%, ${theme.palette.grey[900]}95 100%)`
                    : `linear-gradient(135deg, ${theme.palette.background.paper}98 0%, ${theme.palette.grey[50]}95 100%)`,
              },
            },
          }}
        >
          {/* Animated Background Glow - Much More Subtle */}
          <Box
            className="pantry-glow"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: config.gradient,
              opacity: 0,
              transition: "opacity 0.3s ease",
              zIndex: 1,
            }}
          />

          {/* Status Indicator Strip */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 6,
                background: stockStatus.color,
                boxShadow: `0 0 20px ${stockStatus.color}60`,
                zIndex: 2,
              }}
            />
          </motion.div>

          {/* Stock Badge */}
          <Badge
            badgeContent={count}
            max={99}
            color={
              stockStatus.severity === "critical"
                ? "error"
                : stockStatus.severity === "warning"
                ? "warning"
                : "success"
            }
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 3,
              "& .MuiBadge-badge": {
                fontSize: "0.75rem",
                fontWeight: 700,
                height: 24,
                minWidth: 24,
                borderRadius: 3,
                boxShadow: `0 2px 8px ${stockStatus.color}40`,
              },
            }}
          >
            <motion.div
              className="status-indicator"
              whileHover={{ scale: 1.1, rotate: 5 }}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  background: stockStatus.color,
                  boxShadow: `0 4px 16px ${stockStatus.color}40`,
                }}
              >
                <StatusIcon sx={{ color: "white", fontSize: 24 }} />
              </Avatar>
            </motion.div>
          </Badge>

          {/* Enhanced Image Section */}
          <Box sx={{ position: "relative", height: 160 }}>
            {!imageError ? (
              <CardMedia
                component="img"
                height={160}
                image={imageUrl}
                alt={title}
                onError={() => setImageError(true)}
                sx={{
                  objectFit: "cover",
                  filter:
                    theme.palette.mode === "dark"
                      ? "brightness(0.8)"
                      : "brightness(1)",
                  transition: "filter 0.3s ease",
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: config.gradient,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Pattern Background */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    fontSize: "4rem",
                    opacity: 0.1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {config.bgPattern}
                </Box>

                <IconComponent
                  sx={{
                    fontSize: 64,
                    color: "white",
                    filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                    zIndex: 1,
                  }}
                />
              </Box>
            )}

            {/* Overlay Gradient */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "50%",
                background: `linear-gradient(to top, ${config.color}40, transparent)`,
              }}
            />
          </Box>

          {/* Enhanced Content Section */}
          <CardContent
            className="card-content"
            sx={{
              p: 2,
              position: "relative",
              zIndex: 2,
              transition: "background 0.3s ease",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <IconComponent
                  sx={{
                    fontSize: 20,
                    color: config.color,
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: isSmallScreen ? "1rem" : "1.1rem",
                    background:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(45deg, ${theme.palette.text.primary}, ${config.color})`
                        : `linear-gradient(45deg, ${theme.palette.text.primary}, ${config.color})`,
                    WebkitBackgroundClip: "text",
                  }}
                >
                  {title}
                </Typography>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500, opacity: 0.8 }}
              >
                {hasComprehensiveData
                  ? `${totalBoxes} boxes • ${
                      boxes?.filter((box) => box.box_filled > 0).length || 0
                    } active`
                  : config.category}
              </Typography>
            </Box>

            {/* Stock Information */}
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {hasComprehensiveData ? "Fill Level" : "Stock Level"}
                </Typography>
                <Chip
                  label={stockStatus.label}
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: `${stockStatus.color}15`,
                    color: stockStatus.color,
                    border: `1px solid ${stockStatus.color}30`,
                  }}
                />
              </Box>

              {/* Stock Progress Bar */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {hasComprehensiveData
                      ? `${Math.round(totalFilled || 0)}/${Math.round(
                          totalCapacity || 0
                        )} units`
                      : `${count} ${config.unit}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(stockPercentage)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stockPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      background: config.gradient,
                    },
                  }}
                />
              </Box>

              {/* Quick Actions */}
              <Box sx={{ pt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontStyle: "italic",
                    "&::before": { content: '"📦 "' },
                  }}
                >
                  Click to manage pantry
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
);

export default PantryCard;
